import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import moment from 'moment'
import React, { useState } from 'react'
import AnDateTimePicker from '../../../../../components/DateTime/DateTimePicker'
import CustomSelect from '../CustomSelect'
import AssessmentText from '../AssessmentText'

const GRADES = ['Negative', 'Trace', '+', '++', '+++']

// Field keys double as display labels in the generic assess renderers, so
// they're written exactly as they should read on the record. Mirrored in
// mobile/src/components/EPCR/panels/Assess/POC.jsx - keep the two in step.
// Avoid unicode sub/superscripts (pCO₂) - they tofu in the PDF's Helvetica.
export const POC_TESTS = {
  'Pregnancy (hCG)': [
    { label: 'Result', options: ['Positive', 'Negative', 'Invalid'] },
  ],
  'COVID-19': [
    { label: 'Result', options: ['Positive', 'Negative', 'Invalid'] },
  ],
  'Influenza A/B': [
    {
      label: 'Result',
      options: ['Negative', 'Positive - Flu A', 'Positive - Flu B', 'Invalid'],
    },
  ],
  'Strep A': [
    { label: 'Result', options: ['Positive', 'Negative', 'Invalid'] },
  ],
  Urinalysis: [
    { label: 'Leukocytes', options: GRADES },
    { label: 'Nitrites', options: ['Negative', 'Positive'] },
    { label: 'Protein', options: GRADES },
    { label: 'Blood', options: GRADES },
    { label: 'Ketones', options: GRADES },
    { label: 'Glucose', options: GRADES },
    { label: 'Bilirubin', options: GRADES },
    { label: 'Urobilinogen', options: ['Normal', 'Raised'] },
    { label: 'pH' },
    { label: 'Specific Gravity' },
  ],
  'Blood Gas': [
    { label: 'Sample', options: ['Arterial', 'Venous', 'Capillary'] },
    { label: 'pH' },
    { label: 'pCO2 (kPa)' },
    { label: 'pO2 (kPa)' },
    { label: 'HCO3 (mmol/L)' },
    { label: 'Base Excess (mmol/L)' },
    { label: 'Lactate (mmol/L)' },
    { label: 'Glucose (mmol/L)' },
  ],
  'Cardiac Markers': [
    {
      label: 'Marker',
      options: ['Troponin I', 'Troponin T', 'BNP / NT-proBNP', 'CK-MB'],
    },
    { label: 'Result', options: ['Positive', 'Negative', 'Indeterminate'] },
    { label: 'Value', placeholder: 'Include units, e.g. 12 ng/L' },
  ],
  'Blood Ketones': [{ label: 'Value (mmol/L)' }],
  'D-Dimer': [
    { label: 'Result', options: ['Positive', 'Negative'] },
    { label: 'Value' },
  ],
  INR: [{ label: 'Value' }],
  Haemoglobin: [{ label: 'Value (g/L)' }],
  Other: [{ label: 'Test Name' }, { label: 'Result' }],
}

const POC = ({ submitModule }) => {
  const [moduleValues, setModuleValues] = useState({
    dateTime: moment(),
  })

  const test = moduleValues['Test']

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} lg={6}>
        <Typography variant="title2">Point of Care Testing</Typography>
      </Grid>
      <Grid item xs={12} lg={6}>
        <AnDateTimePicker
          date={moduleValues.dateTime}
          fullWidth
          size="small"
          onChange={(newDate) =>
            setModuleValues((prev) => ({ ...prev, dateTime: newDate }))
          }
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <FormControl fullWidth>
          <InputLabel>Test</InputLabel>
          <Select
            value={test ?? ''}
            label="Test"
            onChange={(e) =>
              // Changing test resets its fields so stale values from the
              // previous test don't linger on the entry
              setModuleValues((prev) => ({
                dateTime: prev.dateTime,
                Test: e.target.value,
              }))
            }
          >
            {Object.keys(POC_TESTS).map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      {(POC_TESTS[test] ?? []).map((field) =>
        field.options ? (
          <CustomSelect
            key={field.label}
            moduleValues={moduleValues}
            setModuleValues={setModuleValues}
            label={field.label}
            options={field.options}
          />
        ) : (
          <Grid item xs={12} lg={4} key={field.label}>
            <TextField
              value={moduleValues?.[field.label] ?? ''}
              label={field.label}
              placeholder={field.placeholder}
              fullWidth
              autoComplete='off'
              onChange={(e) =>
                setModuleValues((prev) => ({
                  ...prev,
                  [field.label]: e.target.value,
                }))
              }
            />
          </Grid>
        ),
      )}
      {test && (
        <Grid item xs={12} lg={4}>
          <TextField
            value={moduleValues?.['Device / Lot Number'] ?? ''}
            label="Device / Lot Number"
            fullWidth
            autoComplete='off'
            onChange={(e) =>
              setModuleValues((prev) => ({
                ...prev,
                'Device / Lot Number': e.target.value,
              }))
            }
          />
        </Grid>
      )}
      <AssessmentText
        moduleValues={moduleValues}
        setModuleValues={setModuleValues}
      />
      <Grid item xs={12}>
        <Button
          variant="contained"
          fullWidth
          color="success"
          disabled={!test}
          onClick={() => submitModule('POC', moduleValues)}
        >
          Submit
        </Button>
      </Grid>
    </Grid>
  )
}

export default POC
